import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { Candidate } from './types'
import CloseIcon from '@mui/icons-material/Close'
import { Document, Page, pdfjs } from 'react-pdf'
import React from 'react'
import { getMediaUrl } from '@assembly/utils'
import Markdown from 'react-markdown'
import 'react-pdf/dist/Page/TextLayer.css'
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

type ResumeDialogProps = {
  open: boolean
  onClose: () => void
  candidate: Candidate | null
}

export default function ResumeDialog({
  open,
  onClose,
  candidate,
}: ResumeDialogProps) {
  const resumeUrl = getMediaUrl(candidate?.resume?.media as string)
  const fileName = candidate?.resume?.name || ''

  // Determine if the file is a text file based on extension
  const isTextFile = fileName.toLowerCase().endsWith('.txt')

  const renderViewer = () => {
    if (!resumeUrl) {
      return (
        <Box sx={{ padding: 2, textAlign: 'center' }}>
          <Typography color="error">No resume file available</Typography>
        </Box>
      )
    }

    if (isTextFile) {
      return <TextViewer textURL={resumeUrl} />
    } else {
      return <PDFViewer pdfURL={resumeUrl} />
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Resume ({candidate?.firstName} {candidate?.lastName})
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {renderViewer()}
      </DialogContent>
    </Dialog>
  )
}

function PDFViewer(props: any) {
  const [numPages, setNumPages] = React.useState<number>()

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages)
  }

  return (
    <Document file={props.pdfURL} onLoadSuccess={onDocumentLoadSuccess}>
      {Array.from(new Array(numPages), (el, index) => (
        <Box>
          <Page
            renderAnnotationLayer={false}
            width={850}
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            renderTextLayer={false}
          />
          <Divider />
        </Box>
      ))}
    </Document>
  )
}

function TextViewer(props: { textURL: string }) {
  const [textContent, setTextContent] = React.useState<string>('')
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string>('')

  React.useEffect(() => {
    if (!props.textURL) {
      setError('No file URL provided')
      setLoading(false)
      return
    }

    const fetchTextContent = async () => {
      try {
        setLoading(true)
        setError('')

        const response = await fetch(props.textURL)
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`)
        }

        const text = await response.text()
        setTextContent(text)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load file')
      } finally {
        setLoading(false)
      }
    }

    fetchTextContent()
  }, [props.textURL])

  if (loading) {
    return (
      <Box sx={{ padding: 2, textAlign: 'center' }}>
        <Typography>Loading resume...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography color="error">Error loading resume: {error}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{
      padding: 2,
      backgroundColor: '#fafafa',
      borderRadius: 1,
      maxHeight: '70vh',
      overflow: 'auto'
    }}>
      <Typography component="div">
        <Markdown>{textContent}</Markdown>
      </Typography>
    </Box>
  )
}
